import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2, Send, PhoneCall, Lock, StickyNote } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getNotesForIdentity,
  addContactNote,
  deleteContactNote,
} from '../../../api/contact';
import {
  getAttendeeNotes,
  addAttendeeNote,
  deleteAttendeeNote,
} from '../../../api/inspection';
import ReminderPresets from './ReminderPresets';
import NotePreferenceSuggestions from './NotePreferenceSuggestions';
import AgentNotesSummary from '../../../components/notes/AgentNotesSummary';
import {
  useLinkedNotes,
  LinkedNotesSection,
  linkedToSummaryNotes,
} from '../../../components/notes/linkedNotes';

/**
 * One buyer's note thread: every note ever written about them, each stamped
 * with the agent who wrote it and when, newest first, plus a box to add another.
 *
 * Deliberately append-only. Notes used to be a single overwritable box on the
 * open-home attendee row, so a second note wiped the first and nothing recorded
 * who wrote it — which is why notes taken at an open home never matched what
 * the property tab showed (Christian Gravias, 2026-08-18).
 *
 * Renders bare (no modal chrome) so the same thread can sit inline in the Open
 * Home qualify drawer and inside the ContactNotesButton popup, guaranteeing
 * both surfaces show identical notes.
 *
 * Props:
 *   identity     { contactId?, attendeeId?, buyerId?, email?, name?, phone? }.
 *                `attendeeId` routes through the attendee endpoint, which also
 *                links a contact for walk-ins who don't have one yet and folds
 *                any older attendee notes/feedback into the thread.
 *   onNoteChange called with the newest note (or null) whenever it changes, so
 *                a parent list row can restamp itself without a refetch.
 *   onLoaded     called with { contactId, contactName } once resolved.
 *   showReminder set false to hide the "remind me to call again" presets.
 *   suggestPreferences  true on buyer surfaces: after a note is saved, offer
 *                the buying preferences it mentions (Monday 13391227323).
 *                Off by default so a seller's "$1.4m" never becomes a budget.
 *   onPreferencesApplied  called with the rebuilt buyer_preferences snapshot.
 */
const toLatestNote = (n) =>
  n ? { id: n.id, text: n.text, author: n.author_name, created_at: n.created_at } : null;

const ContactNotesThread = ({
  identity = {},
  onNoteChange,
  onLoaded,
  showReminder = true,
  autoFocus = false,
  suggestPreferences = false,
  onPreferencesApplied,
}) => {
  const attendeeId = identity.attendeeId || null;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contactId, setContactId] = useState(identity.contactId || null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [error, setError] = useState(false);
  const [lastContacted, setLastContacted] = useState(null);
  // The note most recently added in this session — its preset reminders link to it.
  const [lastNote, setLastNote] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = attendeeId
        ? await getAttendeeNotes(attendeeId)
        : await getNotesForIdentity(identity);
      setContactId(res.data.contact_id);
      setNotes(res.data.notes || []);
      setLastContacted(res.data.last_contacted || null);
      onLoaded?.({
        contactId: res.data.contact_id,
        contactName: res.data.contact_name || identity.name || '',
      });
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
    // `identity` is a fresh object literal on most callers' every render, so it
    // must not be a dependency here — that would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendeeId, identity.contactId, identity.email, identity.buyerId]);

  useEffect(() => { load(); }, [load]);

  // Notes about this person from the rest of the CRM - their appraisal, Database
  // row and Seller Lead - so a home-page reminder card opens onto the whole
  // history, not just the contact's own thread (CRM-2026-00106).
  const linked = useLinkedNotes(
    contactId ? { contact: contactId, from: 'contact' } : {},
    notes.length
  );

  const handleAdd = async () => {
    const text = newNote.trim();
    if (!text) return;
    if (!attendeeId && !contactId) return;
    setSaving(true);
    try {
      const res = attendeeId
        ? await addAttendeeNote(attendeeId, text)
        : await addContactNote(contactId, { text });
      setNotes((prev) => [res.data, ...prev]);
      setLastNote(res.data);
      if (!contactId && res.data.contact) setContactId(res.data.contact);
      setLastContacted({
        agent_name: res.data.author_name || 'You',
        date: res.data.created_at,
        is_yours: true,
      });
      setNewNote('');
      onNoteChange?.(toLatestNote(res.data));
      toast.success('Note added');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!attendeeId && !contactId) return;
    try {
      if (attendeeId) await deleteAttendeeNote(attendeeId, noteId);
      else await deleteContactNote(contactId, noteId);
      setNotes((prev) => {
        const remaining = prev.filter((n) => n.id !== noteId);
        onNoteChange?.(toLatestNote(remaining[0]));
        return remaining;
      });
      toast.success('Note deleted');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to delete note');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleAdd();
    }
  };

  // The newest note on this person when it is one this agent cannot see in the
  // list below (another agent wrote it). Shown on its own so the panel never
  // claims there are no notes while a row above it displays one.
  const othersLastNote =
    lastContacted?.text && !notes.some((n) => n.id === lastContacted.note_id)
      ? lastContacted
      : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Last contacted — visible to every agent, whoever wrote it */}
      {lastContacted && (
        <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2 text-[11px] text-gray-500">
          <PhoneCall className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          <span>
            Last contacted:{' '}
            <span className="font-semibold text-gray-700">
              {lastContacted.is_yours ? 'You' : lastContacted.agent_name}
            </span>
            {lastContacted.date && (
              <> · {new Date(lastContacted.date).toLocaleDateString()}</>
            )}
          </span>
        </div>
      )}

      {/* The newest note on this person even when another agent wrote it. The
          row this panel opened from already shows it, so a panel that answered
          "No notes yet" underneath was contradicting itself (Paul Jimoulis,
          CRM-2026-00050). The rest of another agent's history stays theirs. */}
      {othersLastNote && (
        <div className="border-b border-amber-100 bg-amber-50 px-4 py-2.5">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Last note</p>
          <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-amber-900">
            {othersLastNote.text}
          </p>
          <p className="mt-1 text-[11px] font-semibold text-amber-700/80">
            {othersLastNote.agent_name}
            {othersLastNote.date && ` · ${new Date(othersLastNote.date).toLocaleString('en-AU', {
              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
            })}`}
          </p>
        </div>
      )}

      {/* Notes history — rendered ABOVE the composer so previous notes are
          visible straight away. When the composer and the (tall) reminder
          presets sat on top, the history was pushed out of view on a
          height-capped panel and read as empty (CRM-2026-00056).
          It deliberately does NOT scroll on its own: it grows to fit every note
          and the panel around it scrolls, exactly as the Find Buyers popup
          does. Owning the scrollbar meant the composer and reminder presets
          claimed their height first and the history collapsed to a two-line
          window, so a note you could read in full under Find Buyers had to be
          scrolled under a property's Attendees (Joshua Diokno, CRM-2026-00050). */}
      <div
        data-testid="previous-notes-panel"
        className="flex min-h-[140px] flex-col border-b border-gray-100 p-4"
      >
        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <StickyNote className="h-3.5 w-3.5 text-teal-500" /> Agent Notes
        </div>
        {/* AI summary of the notes shown here (Monday 13202770775) */}
        {!loading && !error && (
          <AgentNotesSummary
            notes={[
              ...notes,
              ...(othersLastNote ? [othersLastNote] : []),
              ...linkedToSummaryNotes(linked),
            ]}
            subject={identity.name}
            className="mb-3"
          />
        )}
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
          </div>
        ) : error ? (
          <div className="py-6 text-center text-sm text-gray-500">
            Couldn't load notes.{' '}
            <button type="button" onClick={load} className="font-medium text-teal-600 hover:underline">
              Retry
            </button>
          </div>
        ) : notes.length === 0 ? (
          linked.length ? null : <p className="py-6 text-center text-sm text-gray-400">
            {othersLastNote ? 'No notes of your own yet.' : 'No notes yet.'}
          </p>
        ) : (
          <div className="space-y-2">
            {notes.map((note) => (
              <div
                key={note.id}
                className="group rounded-xl bg-gray-50 p-3 transition-colors hover:bg-gray-100/80"
              >
                <div className="flex items-center justify-between gap-2">
                  {/* The stamp Christian asked for: who wrote it, and when. Both
                      are recorded server-side at write time and cannot be edited
                      afterwards, so the thread stays a true record. */}
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-bold text-gray-900">{note.author_name || 'Agent'}</span>
                    <span className="text-gray-400">
                      {note.created_at
                        ? new Date(note.created_at).toLocaleString('en-AU', {
                            day: 'numeric', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : ''}
                    </span>
                    {note.is_auto && (
                      <span
                        title="Recorded by the CRM — visible to the whole team"
                        className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
                      >
                        <Lock className="h-2.5 w-2.5" /> Auto
                      </span>
                    )}
                  </div>
                  {/* Only your own notes get a bin. Another agent's note and any
                      system stamp are not yours to remove, and the server enforces
                      the same rule - showing a button that 403s is worse than not
                      showing one (Christian Gravias, 2026-08-19). */}
                  {note.can_delete !== false && (
                    <button
                      type="button"
                      onClick={() => handleDelete(note.id)}
                      className="p-1 text-gray-400 opacity-0 transition-all hover:text-red-500 group-hover:opacity-100"
                      title="Delete note"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                  {note.text}
                </p>
              </div>
            ))}
          </div>
        )}
        {!loading && !error && <LinkedNotesSection notes={linked} className="mt-3" />}
      </div>

      {/* Add note — sits beneath the history, same order and same labelling as
          the Find Buyers popup so the two panels read as one screen. */}
      <div className="flex-shrink-0 p-4">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <StickyNote className="h-3.5 w-3.5 text-amber-500" /> Log a call / note
        </div>
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          placeholder="Add a note about this buyer…"
          rows={3}
          className="w-full resize-none rounded-lg border border-gray-200 p-2.5 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
        />
        <div className="mt-2 flex items-center justify-between">
          {/* Christian asked twice why a second note looked like it replaced the
              first, so the panel now states what Save does. */}
          <span className="text-[11px] text-gray-400">
            Adds to the notes above, never replaces them · ⌘/Ctrl + Enter to save
          </span>
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving || !newNote.trim() || loading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-teal-600 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Save Note
          </button>
        </div>

        {suggestPreferences && contactId && lastNote?.text && (
          <NotePreferenceSuggestions
            key={lastNote.id}
            contactId={contactId}
            noteText={lastNote.text}
            onApplied={onPreferencesApplied}
          />
        )}

        {/* Schedule a follow-up call — surfaces in the agent's Who-to-Call list */}
        {showReminder && contactId && (
          <div className="mt-3">
            <ReminderPresets
              contactId={contactId}
              noteId={lastNote?.id || null}
              noteText={lastNote?.text || ''}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactNotesThread;
